import type { Hotkey } from "@tanstack/react-hotkeys";
import { getHotkeyManager } from "@tanstack/react-hotkeys";
import { useNavigate } from "@tanstack/react-router";
import { useEffect } from "react";
import { paletteStore } from "@/app/command-palette/palette-store";
import { dispatchCommand } from "./dispatch-command";
import { resolveHotkey } from "./hotkey-store";
import { commands } from "./registry";
import type { CommandContext } from "./types";
import { useHotkeyBindings } from "./use-hotkey-store";

/**
 * Registers every command's resolved chord in one effect against the
 * `HotkeyManager` singleton, and unregisters all of them on cleanup.
 * Replaces `CommandHotkeyBinder`, which mounted a gate-plus-binding pair per
 * command — forty-eight components that each subscribed to the bindings
 * store and rendered nothing.
 */
export function useCommandHotkeys(ctx: CommandContext): void {
  const bindings = useHotkeyBindings();
  const navigate = useNavigate();

  useEffect(() => {
    const manager = getHotkeyManager();
    const handles = commands.flatMap((command) => {
      const hotkey = resolveHotkey(command.id, bindings);
      if (!hotkey) return [];

      return [
        manager.register(hotkey as Hotkey, () =>
          dispatchCommand(command, {
            navigate: (opts) => void navigate(opts),
            // A hotkey fires with the palette closed (or on another view), so
            // switching view always opens the palette there — unlike dispatch
            // from inside the already-open palette, which only needs to change
            // the view.
            setPaletteView: (view) => paletteStore.send({ type: "palette.open", view }),
            closePalette: () => paletteStore.send({ type: "palette.close" }),
            ctx,
          }),
        ),
      ];
    });

    return () => {
      for (const handle of handles) {
        if (handle.isActive) handle.unregister();
      }
    };
  }, [bindings, navigate, ctx]);
}
